import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLNavigation = () => {
    const basicNavCode = `<nav>
    <ul>
        <li><a href="/">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/blog">Blog</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>`;

    const horizontalNavCode = `<nav class="horizontal-nav">
    <ul>
        <li><a href="/" class="active">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<style>
.horizontal-nav ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 2rem;
    background-color: #333;
}

.horizontal-nav li {
    margin: 0;
}

.horizontal-nav a {
    display: block;
    padding: 1rem 1.5rem;
    color: white;
    text-decoration: none;
    transition: background-color 0.3s;
}

.horizontal-nav a:hover,
.horizontal-nav a:focus {
    background-color: #555;
}

.horizontal-nav a.active {
    background-color: #007bff;
}
</style>`;

    const dropdownNavCode = `<nav class="dropdown-nav">
    <ul>
        <li><a href="/">Home</a></li>
        <li>
            <a href="/services">Services</a>
            <ul class="dropdown">
                <li><a href="/services/web-design">Web Design</a></li>
                <li><a href="/services/development">Development</a></li>
                <li><a href="/services/seo">SEO</a></li>
            </ul>
        </li>
        <li>
            <a href="/about">About</a>
            <ul class="dropdown">
                <li><a href="/about/team">Our Team</a></li>
                <li><a href="/about/history">History</a></li>
            </ul>
        </li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<style>
.dropdown-nav ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.dropdown-nav &gt; ul {
    display: flex;
    background-color: #333;
}

.dropdown-nav &gt; ul &gt; li {
    position: relative;
}

.dropdown-nav &gt; ul &gt; li &gt; a {
    display: block;
    padding: 1rem 1.5rem;
    color: white;
    text-decoration: none;
}

/* Dropdown submenu */
.dropdown {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 200px;
    background-color: #444;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

/* Show dropdown on hover and focus */
.dropdown-nav li:hover &gt; .dropdown,
.dropdown-nav li:focus-within &gt; .dropdown {
    display: block;
}

.dropdown li a {
    padding: 0.75rem 1.5rem;
    color: white;
    display: block;
    text-decoration: none;
}

.dropdown li a:hover {
    background-color: #555;
}
</style>`;

    const hamburgerMenuCode = `<nav class="mobile-nav">
    <button class="hamburger" aria-label="Toggle menu" aria-expanded="false">
        <span></span>
        <span></span>
        <span></span>
    </button>
    
    <ul class="nav-menu">
        <li><a href="/">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/services">Services</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<style>
/* Mobile-first approach */
.hamburger {
    display: block;
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
}

.hamburger span {
    display: block;
    width: 25px;
    height: 3px;
    background-color: #333;
    margin: 5px 0;
    transition: 0.3s;
}

.nav-menu {
    display: none;
    list-style: none;
    margin: 0;
    padding: 0;
}

.nav-menu.active {
    display: block;
}

.nav-menu li a {
    display: block;
    padding: 1rem;
    text-decoration: none;
    color: #333;
    border-bottom: 1px solid #eee;
}

/* Desktop view */
@media (min-width: 768px) {
    .hamburger {
        display: none;
    }
    
    .nav-menu {
        display: flex !important;
        gap: 2rem;
    }
    
    .nav-menu li a {
        border-bottom: none;
    }
}
</style>

<script>
const hamburger = document.querySelector('.hamburger');
const navMenu = document.querySelector('.nav-menu');

hamburger.addEventListener('click', () => {
    navMenu.classList.toggle('active');
    const expanded = hamburger.getAttribute('aria-expanded') === 'true';
    hamburger.setAttribute('aria-expanded', !expanded);
});

// Close menu when link is clicked
navMenu.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
        navMenu.classList.remove('active');
        hamburger.setAttribute('aria-expanded', 'false');
    });
});
</script>`;

    const ariaCurrentCode = `<nav>
    <ul>
        <li><a href="/" aria-current="page">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<style>
/* Style current page */
nav a[aria-current="page"] {
    font-weight: bold;
    background-color: #007bff;
    color: white;
}
</style>`;

    const activeClassCode = `<nav>
    <ul>
        <li><a href="/" class="active">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<style>
nav a.active {
    font-weight: bold;
    color: #007bff;
    border-bottom: 3px solid #007bff;
}
</style>`;

    const breadcrumbsCode = `<nav aria-label="Breadcrumb">
    <ol>
        <li><a href="/">Home</a></li>
        <li><a href="/blog">Blog</a></li>
        <li><a href="/blog/html">HTML</a></li>
        <li aria-current="page">Navigation Menus</li>
    </ol>
</nav>

<style>
nav[aria-label="Breadcrumb"] ol {
    list-style: none;
    padding: 0;
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

nav[aria-label="Breadcrumb"] li {
    display: flex;
    align-items: center;
}

nav[aria-label="Breadcrumb"] li:not(:last-child)::after {
    content: "";
    margin-left: 0.5rem;
    color: #666;
}

nav[aria-label="Breadcrumb"] a {
    color: #007bff;
    text-decoration: none;
}

nav[aria-label="Breadcrumb"] a:hover {
    text-decoration: underline;
}

nav[aria-label="Breadcrumb"] li[aria-current="page"] {
    color: #666;
    font-weight: normal;
}
</style>`;

    const skipLinkCode = `<body>
    <!-- Skip link should be first focusable element --&gt;
    <a href="#main-content" class="skip-link">Skip to main content</a>
    
    <header>
        <nav>
            <!-- Long navigation menu --&gt;
        </nav>
    </header>
    
    <main id="main-content">
        <!-- Main content --&gt;
    </main>
</body>

<style>
/* Hide skip link off-screen by default */
.skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    background: #000;
    color: #fff;
    padding: 8px;
    text-decoration: none;
    z-index: 100;
}

/* Show skip link when focused */
.skip-link:focus {
    top: 0;
}
</style>`;

    const paginationCode = `<nav aria-label="Pagination">
    <ul>
        <li>
            <a href="?page=1" aria-label="Previous page">
                 Previous
            </a>
        </li>
        <li><a href="?page=1">1</a></li>
        <li><a href="?page=2" aria-current="page">2</a></li>
        <li><a href="?page=3">3</a></li>
        <li><a href="?page=4">4</a></li>
        <li><span>...</span></li>
        <li><a href="?page=10">10</a></li>
        <li>
            <a href="?page=3" aria-label="Next page">
                Next 
            </a>
        </li>
    </ul>
</nav>

<style>
nav[aria-label="Pagination"] ul {
    list-style: none;
    display: flex;
    gap: 0.5rem;
    padding: 0;
    justify-content: center;
}

nav[aria-label="Pagination"] a,
nav[aria-label="Pagination"] span {
    display: block;
    padding: 0.5rem 0.75rem;
    text-decoration: none;
    color: #007bff;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
}

nav[aria-label="Pagination"] a:hover {
    background-color: #e9ecef;
}

nav[aria-label="Pagination"] a[aria-current="page"] {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
}
</style>`;

    const multipleNavsCode = `<!-- Main navigation --&gt;
<nav aria-label="Main navigation">
    <ul>
        <li><a href="/">Home</a></li>
        <li><a href="/about">About</a></li>
    </ul>
</nav>

<!-- Footer navigation --&gt;
<nav aria-label="Footer navigation">
    <ul>
        <li><a href="/privacy">Privacy</a></li>
        <li><a href="/terms">Terms</a></li>
    </ul>
</nav>

<!-- Social media navigation --&gt;
<nav aria-label="Social media">
    <ul>
        <li><a href="https://twitter.com/..." aria-label="Twitter"></a></li>
        <li><a href="https://facebook.com/..." aria-label="Facebook"></a></li>
    </ul>
</nav>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Links & Navigation</div>
                <h1>Building Navigation Menus</h1>
                <p className="hero-lead">
                    Create accessible, user-friendly navigation menus. Master horizontal and vertical menus,
                    dropdowns, mobile navigation, and best practices for site navigation.
                </p>
            </div>

            <div className="content-section">
                <h2>The Importance of Good Navigation</h2>
                <p>
                    Navigation is the roadmap of your website. Good navigation helps users find content quickly
                    and improves both user experience and SEO.
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> User Experience</h3>
                        <p>Users can find content quickly without frustration</p>
                    </div>
                    <div className="benefit-card">
                        <h3> SEO Benefits</h3>
                        <p>Search engines crawl navigation to understand site structure</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Accessibility</h3>
                        <p>Screen readers use navigation as landmarks for quick access</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Conversions</h3>
                        <p>Clear navigation increases engagement and conversions</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Semantic Navigation HTML</h2>
                <p>
                    Always use the <code>&lt;nav&gt;</code> element for major navigation blocks:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Navigation Structure</span>
                    </div>
                    <CodeBlock language="html" code={basicNavCode} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Why use lists for navigation?</strong>
                        <ul>
                            <li>Semantic grouping of related links</li>
                            <li>Screen readers announce "list with 5 items"</li>
                            <li>Easy to style and maintain</li>
                            <li>Natural fallback if CSS fails</li>
                        </ul>
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Horizontal Navigation Menu</h2>
                <p>
                    The most common navigation pattern for desktop websites:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>HTML + CSS Horizontal Menu</span>
                    </div>
                    <CodeBlock language="html" code={horizontalNavCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Dropdown Menus</h2>
                <p>
                    Multi-level navigation with submenus:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Accessible Dropdown Menu</span>
                    </div>
                    <CodeBlock language="html" code={dropdownNavCode} />
                </div>

                <Warning><strong> Accessibility Warning:</strong> Hover-only dropdowns are not keyboard-accessible!
                    The <code>:focus-within</code> CSS pseudo-class helps, but consider adding JavaScript
                    for full keyboard support and ARIA attributes.</Warning>
            </div>

            <div className="content-section">
                <h2>Mobile-Friendly Navigation (Hamburger Menu)</h2>
                <p>
                    Responsive navigation that collapses on mobile:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Hamburger Menu with JavaScript</span>
                    </div>
                    <CodeBlock language="html" code={hamburgerMenuCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Current Page Indication</h2>
                <p>
                    Always indicate which page the user is currently on:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Using aria-current Attribute</span>
                    </div>
                    <CodeBlock language="html" code={ariaCurrentCode} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Alternative: Using Class</span>
                    </div>
                    <CodeBlock language="html" code={activeClassCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Breadcrumb Navigation</h2>
                <p>
                    Shows the user's current location in the site hierarchy:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Semantic Breadcrumbs</span>
                    </div>
                    <CodeBlock language="html" code={breadcrumbsCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Skip Navigation Link</h2>
                <p>
                    Essential for keyboard users to skip repetitive navigation:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Skip Link Implementation</span>
                    </div>
                    <CodeBlock language="html" code={skipLinkCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Pagination Navigation</h2>
                <p>
                    Navigate through multiple pages of content:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Accessible Pagination</span>
                    </div>
                    <CodeBlock language="html" code={paginationCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Multiple Navigation Sections</h2>
                <p>
                    When you have multiple <code>&lt;nav&gt;</code> elements, label them:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Using aria-label for Multiple Navs</span>
                    </div>
                    <CodeBlock language="html" code={multipleNavsCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Navigation Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use <code>&lt;nav&gt;</code> element for major navigation</li>
                            <li>Keep navigation consistent across pages</li>
                            <li>Limit top-level items to 5-7 for usability</li>
                            <li>Indicate current page with <code>aria-current</code></li>
                            <li>Provide skip navigation links</li>
                            <li>Make navigation keyboard-accessible</li>
                            <li>Test on mobile devices</li>
                            <li>Use clear, descriptive link text</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use <code>&lt;nav&gt;</code> for every group of links</li>
                            <li>Create mega-menus without proper testing</li>
                            <li>Use hover-only dropdowns (not keyboard accessible)</li>
                            <li>Make navigation too deep (limit to 2-3 levels)</li>
                            <li>Forget mobile responsiveness</li>
                            <li>Use vague link text ("Click here")</li>
                            <li>Remove focus indicators</li>
                            <li>Auto-expand all dropdowns on mobile</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Your Navigation</h2>
                <div className="testing-checklist">
                    <h3>Navigation Checklist:</h3>
                    <ul>
                        <li> Can navigate using only keyboard (Tab, Enter, Arrow keys)</li>
                        <li> Focus indicators are visible</li>
                        <li> Current page is clearly indicated</li>
                        <li> Works on mobile devices (touch-friendly)</li>
                        <li> Screen reader announces navigation correctly</li>
                        <li> Links have descriptive text</li>
                        <li> Dropdown menus are keyboard-accessible</li>
                        <li> Navigation is consistent across all pages</li>
                        <li> All links work and point to correct pages</li>
                        <li> Navigation doesn't overlap content</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLNavigation;


